import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  canAccessRouteName,
  getFirstAllowedRouteName,
  hasAnyRole as hasAnyUserRole,
  hasRole as hasUserRole,
  normalizeRoleCodes,
  type UserRole,
} from '@/router/access'
import { authApi } from '@/features/auth/api/auth.api'
import { usersApi } from '@/features/admin/api/users.api'
import { filesApi } from '@/shared/api/files.api'
import { clearTokens, hasTokens, setTokens } from '@/shared/api/auth-tokens'
import type {
  ChangePasswordRequest,
  LoginRequest,
  UserStatus,
} from '@/features/auth/domain/auth.types'

export type { UserRole }

export type AuthUser = {
  id: number
  username: string
  fullName: string
  phone: string
  status: UserStatus
  avatar: string | null
  memberSince: string | null
  lastLoginAt: string | null
  roles: UserRole[]
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const permissions = ref<string[]>([])
  // Raw backend roles ({ code, name }) straight from /auth/me. `roleCodes` drives sidebar-group
  // marker gating (configuration_manager / access_manager), which the four legacy `userRoles`
  // codes can't express; `roleNames` (derived) feeds UI labels.
  const roleList = ref<Array<{ code: string; name: string }>>([])
  const mustChangePassword = ref(false)
  const loading = ref(false)
  const sessionLoaded = ref(false)

  const isAuthenticated = computed(() => Boolean(user.value))
  const userRoles = computed<UserRole[]>(() => user.value?.roles ?? [])
  const roleCodes = computed(() => roleList.value.map((role) => role.code))
  const roleNames = computed(() => roleList.value.map((role) => role.name))
  const firstAllowedRouteName = computed(() =>
    getFirstAllowedRouteName(userRoles.value, permissions.value, roleCodes.value),
  )

  const hasRole = (role: UserRole) => hasUserRole(userRoles.value, role)
  const hasAnyRole = (roles: readonly UserRole[]) => hasAnyUserRole(userRoles.value, roles)
  const hasRoleCode = (code: string) => roleCodes.value.includes(code)
  const canAccessRoute = (routeName: string) =>
    canAccessRouteName(routeName, userRoles.value, permissions.value, roleCodes.value)
  // Forward-compat for the later operation-code gating phase (not wired to routing yet).
  const hasPermission = (code: string) => permissions.value.includes(code)

  const loadMe = async () => {
    const me = await authApi.me()

    user.value = {
      id: me.user.id,
      username: me.user.username,
      fullName: me.user.fullName,
      phone: me.user.phone,
      status: me.user.status,
      avatar: me.user.avatar,
      memberSince: me.user.memberSince,
      lastLoginAt: me.user.lastLoginAt,
      roles: normalizeRoleCodes(me.roles),
    }
    permissions.value = me.permissions
    roleList.value = me.roles.map((role) => ({ code: role.code, name: role.name }))
    mustChangePassword.value = me.mustChangePassword
  }

  const clearSession = () => {
    clearTokens()
    user.value = null
    permissions.value = []
    roleList.value = []
    mustChangePassword.value = false
  }

  const login = async ({ username, password }: LoginRequest) => {
    loading.value = true

    try {
      const pair = await authApi.login({ username, password })
      setTokens(pair)
      mustChangePassword.value = pair.mustChangePassword
      // /me works even during a temporary password (self-service), so this is safe here.
      await loadMe()
    } finally {
      loading.value = false
    }
  }

  const changePassword = async ({ currentPassword, newPassword }: ChangePasswordRequest) => {
    loading.value = true

    try {
      const pair = await authApi.changePassword({ currentPassword, newPassword })
      // Returns a fresh pair; all prior sessions are revoked — replace tokens, no re-login.
      setTokens(pair)
      mustChangePassword.value = false
      await loadMe()
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch {
      // Clear the local session regardless of the response (auth.md §4.5).
    }

    clearSession()
  }

  // Single-flight boot restore: main.ts and the router guard both call this on startup; without
  // sharing one in-flight promise they would each fire /auth/me before `sessionLoaded` flips.
  let sessionLoadPromise: Promise<void> | null = null
  const ensureSessionLoaded = () => {
    if (sessionLoaded.value) return Promise.resolve()
    if (sessionLoadPromise) return sessionLoadPromise

    sessionLoadPromise = (async () => {
      if (!hasTokens()) return

      try {
        // The http interceptor handles refresh-once + retry; a throw here means refresh failed.
        await loadMe()
      } catch {
        clearSession()
      }
    })().finally(() => {
      sessionLoaded.value = true
      sessionLoadPromise = null
    })

    return sessionLoadPromise
  }

  const uploadAvatar = async (file: File) => {
    if (!user.value) return
    loading.value = true
    try {
      const { hashId } = await filesApi.upload(file, 'PUBLIC')
      await usersApi.setAvatar(user.value.id, hashId)
      await loadMe()
    } finally {
      loading.value = false
    }
  }

  const removeAvatar = async () => {
    if (!user.value) return
    loading.value = true
    try {
      await usersApi.deleteAvatar(user.value.id)
      await loadMe()
    } finally {
      loading.value = false
    }
  }

  return {
    user,
    permissions,
    roleList,
    roleNames,
    roleCodes,
    mustChangePassword,
    loading,
    sessionLoaded,
    userRoles,
    isAuthenticated,
    firstAllowedRouteName,
    hasRole,
    hasAnyRole,
    hasRoleCode,
    canAccessRoute,
    hasPermission,
    login,
    changePassword,
    logout,
    loadMe,
    ensureSessionLoaded,
    clearSession,
    uploadAvatar,
    removeAvatar,
  }
})
